/**
 * WeaveIn.ai Platform - Employer Benefits Dashboard
 * Self-service dashboard for employers to manage benefits, view enrollments, and compare plans
 * Integrates with backend API for real employer plan data
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Building2,
  Users,
  Building,
  DollarSign,
  TrendingUp,
  Download,
  Pencil,
  Info,
  CheckCircle,
  X,
  HelpCircle,
  Pill,
  Eye,
  Plus,
  Trash2,
  Settings,
  Landmark,
  HeartPulse,
  Clock,
  Filter,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import PharmacyBenefitsManager from './PharmacyBenefitsManager';
import {
  healthPlansAPI,
  EmployerDashboardData,
  EmployerPlanSummary,
  EmployerEnrollmentStats,
} from '@/services/api/healthPlans';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// ============================================================================
// Types
// ============================================================================

interface EmployerPlan {
  id: number;
  plan_name: string;
  plan_type: 'medical' | 'dental' | 'vision' | 'life' | 'disability';
  carrier_name: string;
  contract_type: 'fully_insured' | 'self_insured' | 'level_funded';
  effective_date: string;
  renewal_date: string;
  employee_premium: number;
  employer_premium: number;
  deductible_individual: number;
  deductible_family: number;
  oop_max_individual: number;
  oop_max_family: number;
  enrolled_count: number;
  eligible_count: number;
  status: 'active' | 'pending' | 'terminated';
}

interface EnrollmentSummary {
  plan_id: number;
  plan_name: string;
  coverage_tier: string;
  count: number;
  percentage: number;
}

interface CostSummary {
  total_monthly_premium: number;
  employer_contribution: number;
  employee_contribution: number;
  claims_ytd: number;
  projected_annual: number;
  trend_percentage: number;
}

interface BenefitComparison {
  benefit_name: string;
  plans: {
    plan_id: number;
    plan_name: string;
    in_network: string;
    out_network: string;
  }[];
}

interface EmployerBenefitsDashboardProps {
  employerId?: number;  // Made optional - will auto-detect from organization
  employerName?: string;
  onBack?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function EmployerBenefitsDashboard({
  employerId: propEmployerId,
  employerName: propEmployerName = 'Your Organization',
  onBack,
}: EmployerBenefitsDashboardProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolved employer ID and name (from props or auto-detected)
  const [resolvedEmployerId, setResolvedEmployerId] = useState<number | null>(propEmployerId || null);
  const [resolvedEmployerName, setResolvedEmployerName] = useState<string>(propEmployerName);

  // Data state - uses API types when available, falls back to local types for UI
  const [dashboardData, setDashboardData] = useState<EmployerDashboardData | null>(null);
  const [plans, setPlans] = useState<EmployerPlan[]>([]);
  const [enrollmentSummary, setEnrollmentSummary] = useState<EnrollmentSummary[]>([]);
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  const [selectedPlansForComparison, setSelectedPlansForComparison] = useState<number[]>([]);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [benefitComparisons, setBenefitComparisons] = useState<BenefitComparison[]>([]);

  // Plan Setup Dialog States
  const [showHsaFsaDialog, setShowHsaFsaDialog] = useState(false);
  const [showHdhpDialog, setShowHdhpDialog] = useState(false);
  const [showCopayDialog, setShowCopayDialog] = useState(false);
  const [showAccumulatorDialog, setShowAccumulatorDialog] = useState(false);
  const [selectedPlanForConfig, setSelectedPlanForConfig] = useState<EmployerPlan | null>(null);

  // HSA/FSA Configuration State
  const [hsaFsaConfig, setHsaFsaConfig] = useState({
    hsaIndividualLimit: 4300,
    hsaFamilyLimit: 8550,
    catchUpContribution: 1000,
    employerContribution: 500,
    fsaLimit: 3200,
    dependentCareFsaLimit: 5000,
    hsaCustodian: 'HealthEquity',
  });

  // HDHP Configuration State
  const [hdhpConfig, setHdhpConfig] = useState({
    minDeductibleIndividual: 1650,
    minDeductibleFamily: 3300,
    maxOopIndividual: 8300,
    maxOopFamily: 16600,
    preventiveCareExempt: true,
    embeddedDeductible: false,
  });

  // Copay Schedule State
  const [copaySchedule, setCopaySchedule] = useState([
    { service: 'PCP Visit', inNetwork: 25, outNetwork: 40, outNetworkType: 'percent' as const },
    { service: 'Specialist', inNetwork: 50, outNetwork: 40, outNetworkType: 'percent' as const },
    { service: 'Urgent Care', inNetwork: 75, outNetwork: 40, outNetworkType: 'percent' as const },
    { service: 'Emergency Room', inNetwork: 250, outNetwork: 250, outNetworkType: 'copay' as const },
    { service: 'Lab Work', inNetwork: 20, outNetwork: 40, outNetworkType: 'percent' as const },
    { service: 'Imaging', inNetwork: 100, outNetwork: 40, outNetworkType: 'percent' as const },
  ]);

  // Load data from API
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Determine employer ID - use prop if provided, otherwise auto-detect from organization
    let employerId = resolvedEmployerId;
    if (!employerId) {
      try {
        const orgEmployer = await healthPlansAPI.getMyOrganizationEmployer();
        if (orgEmployer.employer_id) {
          employerId = orgEmployer.employer_id;
          setResolvedEmployerId(orgEmployer.employer_id);
          setResolvedEmployerName(orgEmployer.employer_name || propEmployerName);
        } else {
          // No employer linked to organization - show sample data
          console.log('[EmployerBenefits] No employer linked to organization, showing sample data');
          loadSampleData();
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('[EmployerBenefits] Failed to detect organization employer:', err);
        loadSampleData();
        setLoading(false);
        return;
      }
    }

    try {
      // Fetch real data from API
      const data = await healthPlansAPI.getEmployerDashboard(employerId);
      setDashboardData(data);

      // Transform API data to component format
      const transformedPlans: EmployerPlan[] = data.plans.map(p => ({
        id: p.id,
        plan_name: p.plan_name,
        plan_type: mapPlanType(p.plan_type),
        carrier_name: p.carrier_name,
        contract_type: p.contract_type.toLowerCase().replace('_', '_') as any,
        effective_date: p.effective_date || '',
        renewal_date: p.termination_date || '',
        employee_premium: p.employee_premium,
        employer_premium: p.employer_premium,
        deductible_individual: p.deductible_individual,
        deductible_family: p.deductible_family,
        oop_max_individual: p.oop_max_individual,
        oop_max_family: p.oop_max_family,
        enrolled_count: p.enrolled_count,
        eligible_count: data.summary.total_eligible,
        status: p.status.toLowerCase() as 'active' | 'pending' | 'terminated',
      }));

      setPlans(transformedPlans);

      // Transform enrollment data
      const transformedEnrollments: EnrollmentSummary[] = data.enrollments.map(e => ({
        plan_id: e.plan_id,
        plan_name: e.plan_name,
        coverage_tier: formatCoverageTier(e.coverage_tier),
        count: e.count,
        percentage: Math.round((e.count / data.summary.total_enrolled) * 100) || 0,
      }));

      setEnrollmentSummary(transformedEnrollments);

      // Set cost summary
      setCostSummary({
        total_monthly_premium: data.summary.total_monthly_premium,
        employer_contribution: data.summary.employer_contribution,
        employee_contribution: data.summary.employee_contribution,
        claims_ytd: 0, // Not available from current API
        projected_annual: data.summary.total_monthly_premium * 12,
        trend_percentage: 0, // Not available from current API
      });

      // Transform benefit comparisons
      const benefitGroups = new Map<string, BenefitComparison>();
      data.benefits.forEach(b => {
        if (!benefitGroups.has(b.benefit_name)) {
          benefitGroups.set(b.benefit_name, {
            benefit_name: b.benefit_name,
            plans: [],
          });
        }
        benefitGroups.get(b.benefit_name)!.plans.push({
          plan_id: b.plan_id,
          plan_name: b.plan_name,
          in_network: b.in_network,
          out_network: b.out_network,
        });
      });

      setBenefitComparisons(Array.from(benefitGroups.values()));

    } catch (err: any) {
      // Check if it's an "employer not found" error - this is expected for demo/admin users
      const errorMessage = err?.message || err?.response?.data?.detail || '';
      if (errorMessage.toLowerCase().includes('employer not found') || errorMessage.toLowerCase().includes('not found')) {
        console.log('[EmployerBenefits] Employer not found, showing sample data for demo purposes');
        setError(null); // Don't show error for expected "not found" case
      } else {
        console.error('Error loading employer benefits:', err);
        setError('Unable to load live data. Showing sample data.');
      }
      loadSampleData();
    } finally {
      setLoading(false);
    }
  }, [resolvedEmployerId, propEmployerName]);

  // Helper to map plan types
  const mapPlanType = (type: string): 'medical' | 'dental' | 'vision' | 'life' | 'disability' => {
    const planType = type?.toUpperCase();
    if (['HMO', 'PPO', 'EPO', 'POS', 'HDHP', 'CDHP', 'INDEMNITY'].includes(planType)) return 'medical';
    if (planType === 'DENTAL') return 'dental';
    if (planType === 'VISION') return 'vision';
    if (planType === 'LIFE') return 'life';
    if (planType === 'DISABILITY') return 'disability';
    return 'medical';
  };

  // Helper to format coverage tier
  const formatCoverageTier = (tier: string): string => {
    const mapping: Record<string, string> = {
      'EMPLOYEE_ONLY': 'Employee Only',
      'EMPLOYEE_SPOUSE': 'Employee + Spouse',
      'EMPLOYEE_CHILDREN': 'Employee + Children',
      'FAMILY': 'Family',
    };
    return mapping[tier] || tier;
  };

  // Sample data fallback
  const loadSampleData = () => {
    setPlans([
      {
        id: 1,
        plan_name: 'Gold PPO',
        plan_type: 'medical',
        carrier_name: 'Blue Cross Blue Shield',
        contract_type: 'fully_insured',
        effective_date: '2024-01-01',
        renewal_date: '2024-12-31',
        employee_premium: 250,
        employer_premium: 750,
        deductible_individual: 500,
        deductible_family: 1000,
        oop_max_individual: 3000,
        oop_max_family: 6000,
        enrolled_count: 245,
        eligible_count: 300,
        status: 'active',
      },
      {
        id: 2,
        plan_name: 'Silver HDHP',
        plan_type: 'medical',
        carrier_name: 'Blue Cross Blue Shield',
        contract_type: 'self_insured',
        effective_date: '2024-01-01',
        renewal_date: '2024-12-31',
        employee_premium: 150,
        employer_premium: 450,
        deductible_individual: 2000,
        deductible_family: 4000,
        oop_max_individual: 6000,
        oop_max_family: 12000,
        enrolled_count: 55,
        eligible_count: 300,
        status: 'active',
      },
      {
        id: 3,
        plan_name: 'Delta Dental PPO',
        plan_type: 'dental',
        carrier_name: 'Delta Dental',
        contract_type: 'fully_insured',
        effective_date: '2024-01-01',
        renewal_date: '2024-12-31',
        employee_premium: 25,
        employer_premium: 50,
        deductible_individual: 50,
        deductible_family: 150,
        oop_max_individual: 1500,
        oop_max_family: 4500,
        enrolled_count: 280,
        eligible_count: 300,
        status: 'active',
      },
      {
        id: 4,
        plan_name: 'VSP Vision',
        plan_type: 'vision',
        carrier_name: 'VSP',
        contract_type: 'fully_insured',
        effective_date: '2024-01-01',
        renewal_date: '2024-12-31',
        employee_premium: 10,
        employer_premium: 15,
        deductible_individual: 0,
        deductible_family: 0,
        oop_max_individual: 0,
        oop_max_family: 0,
        enrolled_count: 200,
        eligible_count: 300,
        status: 'active',
      },
    ]);

    setEnrollmentSummary([
      { plan_id: 1, plan_name: 'Gold PPO', coverage_tier: 'Employee Only', count: 120, percentage: 49 },
      { plan_id: 1, plan_name: 'Gold PPO', coverage_tier: 'Employee + Spouse', count: 45, percentage: 18 },
      { plan_id: 1, plan_name: 'Gold PPO', coverage_tier: 'Employee + Children', count: 30, percentage: 12 },
      { plan_id: 1, plan_name: 'Gold PPO', coverage_tier: 'Family', count: 50, percentage: 21 },
      { plan_id: 2, plan_name: 'Silver HDHP', coverage_tier: 'Employee Only', count: 35, percentage: 64 },
      { plan_id: 2, plan_name: 'Silver HDHP', coverage_tier: 'Family', count: 20, percentage: 36 },
    ]);

    setCostSummary({
      total_monthly_premium: 285000,
      employer_contribution: 213750,
      employee_contribution: 71250,
      claims_ytd: 1850000,
      projected_annual: 3200000,
      trend_percentage: 5.2,
    });

    setBenefitComparisons([
      {
        benefit_name: 'Primary Care Visit',
        plans: [
          { plan_id: 1, plan_name: 'Gold PPO', in_network: '$20 copay', out_network: '40% after deductible' },
          { plan_id: 2, plan_name: 'Silver HDHP', in_network: '20% after deductible', out_network: '40% after deductible' },
        ],
      },
      {
        benefit_name: 'Specialist Visit',
        plans: [
          { plan_id: 1, plan_name: 'Gold PPO', in_network: '$40 copay', out_network: '40% after deductible' },
          { plan_id: 2, plan_name: 'Silver HDHP', in_network: '20% after deductible', out_network: '40% after deductible' },
        ],
      },
      {
        benefit_name: 'Emergency Room',
        plans: [
          { plan_id: 1, plan_name: 'Gold PPO', in_network: '$250 copay', out_network: '$250 copay' },
          { plan_id: 2, plan_name: 'Silver HDHP', in_network: '20% after deductible', out_network: '20% after deductible' },
        ],
      },
      {
        benefit_name: 'Prescription - Generic',
        plans: [
          { plan_id: 1, plan_name: 'Gold PPO', in_network: '$10 copay', out_network: 'Not covered' },
          { plan_id: 2, plan_name: 'Silver HDHP', in_network: '$10 after deductible', out_network: 'Not covered' },
        ],
      },
    ]);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const togglePlanForComparison = (planId: number) => {
    setSelectedPlansForComparison(prev =>
      prev.includes(planId)
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPlanTypeIcon = (type: string) => {
    switch (type) {
      case 'medical': return <Building className="h-4 w-4" />;
      case 'dental': return <Building className="h-4 w-4" />;
      case 'vision': return <Eye className="h-4 w-4" />;
      case 'life': return <HeartPulse className="h-4 w-4" />;
      default: return <Building className="h-4 w-4" />;
    }
  };

  const getPlanTypeColor = (type: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (type) {
      case 'medical': return 'default';
      case 'dental': return 'secondary';
      case 'vision': return 'outline';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const medicalPlans = plans.filter(p => p.plan_type === 'medical');
  const ancillaryPlans = plans.filter(p => p.plan_type !== 'medical');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-8 w-8 text-primary" />
            {dashboardData?.employer?.name || resolvedEmployerName}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('employer.benefitsDashboard', 'Benefits Dashboard & Plan Management')}
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button
              variant="ghost"
              size="sm"
              className="ml-2"
              onClick={() => setError(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-primary" />
              <p className="text-sm text-muted-foreground">Total Enrolled</p>
            </div>
            <p className="text-3xl font-bold">
              {plans.reduce((sum, p) => sum + p.enrolled_count, 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              of {plans[0]?.eligible_count || 0} eligible employees
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <p className="text-sm text-muted-foreground">Monthly Premium</p>
            </div>
            <p className="text-3xl font-bold">
              {formatCurrency(costSummary?.total_monthly_premium || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Employer: {formatCurrency(costSummary?.employer_contribution || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-orange-600" />
              <p className="text-sm text-muted-foreground">Claims YTD</p>
            </div>
            <p className="text-3xl font-bold">
              {formatCurrency(costSummary?.claims_ytd || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Projected: {formatCurrency(costSummary?.projected_annual || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Building className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-muted-foreground">Active Plans</p>
            </div>
            <p className="text-3xl font-bold">
              {plans.filter(p => p.status === 'active').length}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Across {new Set(plans.map(p => p.plan_type)).size} benefit types
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">{t('employer.plans', 'Plan Overview')}</TabsTrigger>
          <TabsTrigger value="enrollment">{t('employer.enrollment', 'Enrollment')}</TabsTrigger>
          <TabsTrigger value="compare">{t('employer.compare', 'Compare Plans')}</TabsTrigger>
          <TabsTrigger value="pharmacy">{t('employer.pharmacy', 'Pharmacy Benefits')}</TabsTrigger>
          <TabsTrigger value="documents">{t('employer.documents', 'Documents & Resources')}</TabsTrigger>
          <TabsTrigger value="setup">{t('employer.planSetup', 'Plan Setup')}</TabsTrigger>
        </TabsList>

        {/* Plan Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Medical Plans */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Building className="h-5 w-5 text-primary" />
              Medical Plans
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {medicalPlans.map(plan => (
                <Card key={plan.id}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">{plan.plan_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {plan.carrier_name}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="outline">
                          {plan.contract_type.replace('_', ' ')}
                        </Badge>
                        <Badge variant={plan.status === 'active' ? 'default' : 'secondary'}>
                          {plan.status}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Individual Deductible</p>
                        <p className="text-sm sm:text-base font-medium">
                          {formatCurrency(plan.deductible_individual)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Family Deductible</p>
                        <p className="text-sm sm:text-base font-medium">
                          {formatCurrency(plan.deductible_family)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Individual OOP Max</p>
                        <p className="text-sm sm:text-base font-medium">
                          {formatCurrency(plan.oop_max_individual)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Family OOP Max</p>
                        <p className="text-sm sm:text-base font-medium">
                          {formatCurrency(plan.oop_max_family)}
                        </p>
                      </div>
                    </div>

                    <hr className="my-4 border-border" />

                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Employee Premium</p>
                        <p className="text-sm sm:text-base font-medium">
                          {formatCurrency(plan.employee_premium)}/mo
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Employer Premium</p>
                        <p className="text-sm sm:text-base font-medium">
                          {formatCurrency(plan.employer_premium)}/mo
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2 text-xs">
                        <span className="text-muted-foreground">Enrollment</span>
                        <span className="text-muted-foreground">
                          {plan.enrolled_count} / {plan.eligible_count}
                        </span>
                      </div>
                      <Progress
                        value={(plan.enrolled_count / plan.eligible_count) * 100}
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Ancillary Plans */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Building className="h-5 w-5 text-secondary" />
              Ancillary Benefits
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ancillaryPlans.map(plan => (
                <Card key={plan.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Badge variant={getPlanTypeColor(plan.plan_type)}>
                        {plan.plan_type.charAt(0).toUpperCase() + plan.plan_type.slice(1)}
                      </Badge>
                      <Badge
                        variant={plan.status === 'active' ? 'default' : 'outline'}
                      >
                        {plan.status}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{plan.plan_name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {plan.carrier_name}
                    </p>

                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Employee</p>
                        <p className="text-sm font-medium">
                          {formatCurrency(plan.employee_premium)}/mo
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Employer</p>
                        <p className="text-sm font-medium">
                          {formatCurrency(plan.employer_premium)}/mo
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      {plan.enrolled_count} enrolled
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Enrollment Tab */}
        <TabsContent value="enrollment" className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold mb-4">Enrollment by Plan & Coverage Tier</h2>

            {medicalPlans.map(plan => (
              <Card key={plan.id} className="mb-4">
                <CardContent className="pt-6">
                  <h3 className="text-base font-semibold mb-4">
                    {plan.plan_name}
                  </h3>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coverage Tier</TableHead>
                        <TableHead className="text-right">Enrolled</TableHead>
                        <TableHead className="text-right">Percentage</TableHead>
                        <TableHead className="w-[200px]">Distribution</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollmentSummary
                        .filter(e => e.plan_id === plan.id)
                        .map((enrollment, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{enrollment.coverage_tier}</TableCell>
                            <TableCell className="text-right">{enrollment.count}</TableCell>
                            <TableCell className="text-right">{enrollment.percentage}%</TableCell>
                            <TableCell>
                              <Progress
                                value={enrollment.percentage}
                                className="h-2"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Employee Benefit Selections */}
          <hr className="border-border my-8" />

          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Employee Benefit Selections
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export Selections
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/admin/integrations?entityType=benefit_elections')}
              >
                <Download className="h-4 w-4 mr-2" />
                Import Elections
              </Button>
            </div>
          </div>

          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertDescription>
              View individual employee benefit selections, HSA elections, and coverage details. Data is sourced from benefit elections imports.
            </AlertDescription>
          </Alert>

          <Card>
            <CardContent className="pt-6">
              <h3 className="text-base font-semibold mb-4">
                Employee Elections Summary
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Medical Plan</TableHead>
                    <TableHead>Dental</TableHead>
                    <TableHead>Vision</TableHead>
                    <TableHead>HSA Election</TableHead>
                    <TableHead className="text-center">FSA</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { id: 'EMP001', name: 'John Smith', medical: 'Gold PPO', dental: 'Delta PPO', vision: 'VSP', hsa: '$2,400/yr', fsa: false, status: 'Enrolled' },
                    { id: 'EMP002', name: 'Sarah Johnson', medical: 'Silver HDHP', dental: 'Delta PPO', vision: 'VSP', hsa: '$4,300/yr', fsa: false, status: 'Enrolled' },
                    { id: 'EMP003', name: 'Mike Davis', medical: 'Gold PPO', dental: 'Delta PPO', vision: '-', hsa: '-', fsa: true, status: 'Enrolled' },
                    { id: 'EMP004', name: 'Emily Brown', medical: 'Silver HDHP', dental: '-', vision: 'VSP', hsa: '$8,550/yr', fsa: false, status: 'Enrolled' },
                    { id: 'EMP005', name: 'David Wilson', medical: 'Waived', dental: 'Delta PPO', vision: '-', hsa: '-', fsa: false, status: 'Waived' },
                    { id: 'EMP006', name: 'Lisa Anderson', medical: 'Gold PPO', dental: 'Delta PPO', vision: 'VSP', hsa: '$1,200/yr', fsa: false, status: 'Enrolled' },
                    { id: 'EMP007', name: 'James Miller', medical: 'Silver HDHP', dental: 'Delta PPO', vision: 'VSP', hsa: '$4,300/yr', fsa: false, status: 'Enrolled' },
                  ].map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>{employee.id}</TableCell>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>
                        <Badge
                          variant={employee.medical === 'Waived' ? 'outline' : 'default'}
                        >
                          {employee.medical}
                        </Badge>
                      </TableCell>
                      <TableCell>{employee.dental}</TableCell>
                      <TableCell>{employee.vision}</TableCell>
                      <TableCell>
                        <span className={employee.hsa === '-' ? 'text-muted-foreground' : 'text-green-600 font-medium'}>
                          {employee.hsa}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {employee.fsa ? <CheckCircle className="h-4 w-4 text-green-600 inline" /> : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={employee.status === 'Enrolled' ? 'default' : 'outline'}
                        >
                          {employee.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="mt-4 flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Showing 7 of 300 employees
                </p>
                <Button size="sm" variant="ghost">
                  View All Employees
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Benefit Usage Summary */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-base font-semibold mb-4">
                Benefit Usage Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-3xl font-bold text-primary">92%</p>
                  <p className="text-sm text-muted-foreground mt-1">Medical Enrollment</p>
                </div>
                <div className="text-center p-4 bg-secondary/10 rounded-lg">
                  <p className="text-3xl font-bold text-secondary">85%</p>
                  <p className="text-sm text-muted-foreground mt-1">Dental Enrollment</p>
                </div>
                <div className="text-center p-4 bg-green-100 rounded-lg">
                  <p className="text-3xl font-bold text-green-700">68%</p>
                  <p className="text-sm text-muted-foreground mt-1">HSA Participation</p>
                </div>
                <div className="text-center p-4 bg-blue-100 rounded-lg">
                  <p className="text-3xl font-bold text-blue-700">$1.2M</p>
                  <p className="text-sm text-muted-foreground mt-1">Total HSA Contributions YTD</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare Plans Tab */}
        <TabsContent value="compare" className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Plan Comparison
            </h2>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Comparison
            </Button>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Compare medical plans side-by-side to help employees choose the best option for their needs.
            </AlertDescription>
          </Alert>

          <Card>
            <CardContent className="pt-6 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-bold w-[200px]">Benefit</TableHead>
                    {medicalPlans.map(plan => (
                      <TableHead key={plan.id} colSpan={2} className="text-center">
                        <span className="font-semibold">{plan.plan_name}</span>
                      </TableHead>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableHead></TableHead>
                    {medicalPlans.map(plan => (
                      <React.Fragment key={plan.id}>
                        <TableHead className="text-center bg-green-50">
                          <span className="text-xs font-medium">In-Network</span>
                        </TableHead>
                        <TableHead className="text-center bg-orange-50">
                          <span className="text-xs font-medium">Out-of-Network</span>
                        </TableHead>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Deductibles */}
                  <TableRow>
                    <TableCell className="font-medium">Individual Deductible</TableCell>
                    {medicalPlans.map(plan => (
                      <React.Fragment key={plan.id}>
                        <TableCell className="text-center">{formatCurrency(plan.deductible_individual)}</TableCell>
                        <TableCell className="text-center">{formatCurrency(plan.deductible_individual * 2)}</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Family Deductible</TableCell>
                    {medicalPlans.map(plan => (
                      <React.Fragment key={plan.id}>
                        <TableCell className="text-center">{formatCurrency(plan.deductible_family)}</TableCell>
                        <TableCell className="text-center">{formatCurrency(plan.deductible_family * 2)}</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">OOP Maximum (Individual)</TableCell>
                    {medicalPlans.map(plan => (
                      <React.Fragment key={plan.id}>
                        <TableCell className="text-center">{formatCurrency(plan.oop_max_individual)}</TableCell>
                        <TableCell className="text-center">{formatCurrency(plan.oop_max_individual * 2)}</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>

                  {/* Benefits from comparison data */}
                  {benefitComparisons.map((comparison, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{comparison.benefit_name}</TableCell>
                      {comparison.plans.map(planBenefit => (
                        <React.Fragment key={planBenefit.plan_id}>
                          <TableCell className="text-center">{planBenefit.in_network}</TableCell>
                          <TableCell className="text-center">{planBenefit.out_network}</TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))}

                  {/* Premium row */}
                  <TableRow className="bg-muted/50">
                    <TableCell className="font-bold">Monthly Employee Premium</TableCell>
                    {medicalPlans.map(plan => (
                      <TableCell key={plan.id} colSpan={2} className="text-center">
                        <span className="font-semibold text-primary">
                          {formatCurrency(plan.employee_premium)}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pharmacy Benefits Tab */}
        <TabsContent value="pharmacy">
          <PharmacyBenefitsManager
            planId={medicalPlans[0]?.id || 0}
            employerId={resolvedEmployerId || undefined}
            isReadOnly={false}
          />
        </TabsContent>

        {/* Documents & Resources Tab */}
        <TabsContent value="documents" className="space-y-6">
          <h2 className="text-xl font-semibold mb-4">Plan Documents & Resources</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-base font-semibold mb-4">
                  Summary Plan Descriptions (SPDs)
                </h3>
                <hr className="mb-4 border-border" />
                {plans.map(plan => (
                  <div
                    key={plan.id}
                    className="flex justify-between items-center py-2 border-b last:border-b-0"
                  >
                    <div>
                      <p className="text-sm">{plan.plan_name} SPD</p>
                      <p className="text-xs text-muted-foreground">
                        Updated: {fmt.short(plan.effective_date)}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-base font-semibold mb-4">
                  Enrollment Resources
                </h3>
                <hr className="mb-4 border-border" />
                {[
                  'Benefits Guide 2024',
                  'How to Enroll Guide',
                  'Plan Comparison Worksheet',
                  'HSA/FSA Election Forms',
                  'Dependent Verification Forms',
                ].map((doc, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center py-2 border-b last:border-b-0"
                  >
                    <p className="text-sm">{doc}</p>
                    <Button size="sm" variant="ghost">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="pt-6">
                <h3 className="text-base font-semibold mb-4">
                  Provider & Pharmacy Directories
                </h3>
                <hr className="mb-4 border-border" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open('https://www.bcbs.com/find-a-doctor', '_blank')}
                  >
                    <Building className="h-4 w-4 mr-2" />
                    Find a Doctor
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Pill className="h-4 w-4 mr-2" />
                    Find a Pharmacy
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Building className="h-4 w-4 mr-2" />
                    Find a Dentist
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Eye className="h-4 w-4 mr-2" />
                    Find an Eye Doctor
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Plan Setup Tab */}
        <TabsContent value="setup" className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Plan Configuration & Setup
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Configure benefit plan details, cost sharing, and integration settings. Import data from HRIS systems, claims files, and HSA custodians.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* HSA/FSA Configuration */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    HSA/FSA Configuration
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Configure Health Savings Accounts and Flexible Spending Accounts for HDHP-eligible plans.
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm">HSA Individual Limit (2025)</span>
                      <span className="text-sm font-medium">$4,300</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm">HSA Family Limit (2025)</span>
                      <span className="text-sm font-medium">$8,550</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm">Catch-Up (55+)</span>
                      <span className="text-sm font-medium">$1,000</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-sm">Employer Contribution</span>
                      <span className="text-sm font-medium text-green-600">$500/year</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                    onClick={() => setShowHsaFsaDialog(true)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Configure HSA/FSA
                  </Button>
                </CardContent>
              </Card>

              {/* HDHP Requirements */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                    <Building className="h-5 w-5 text-primary" />
                    HDHP Configuration
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    High Deductible Health Plan minimum requirements for HSA eligibility.
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm">Min Deductible (Individual)</span>
                      <span className="text-sm font-medium">$1,650</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm">Min Deductible (Family)</span>
                      <span className="text-sm font-medium">$3,300</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm">Max OOP (Individual)</span>
                      <span className="text-sm font-medium">$8,300</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-sm">Max OOP (Family)</span>
                      <span className="text-sm font-medium">$16,600</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                    onClick={() => setShowHdhpDialog(true)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Configure HDHP
                  </Button>
                </CardContent>
              </Card>

              {/* Copay Schedule */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-orange-600" />
                    Copay Schedule
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Define member cost-sharing for various service types.
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead className="text-right">In-Network</TableHead>
                        <TableHead className="text-right">Out-of-Network</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>PCP Visit</TableCell>
                        <TableCell className="text-right">$25</TableCell>
                        <TableCell className="text-right">40%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Specialist</TableCell>
                        <TableCell className="text-right">$50</TableCell>
                        <TableCell className="text-right">40%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Urgent Care</TableCell>
                        <TableCell className="text-right">$75</TableCell>
                        <TableCell className="text-right">40%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Emergency Room</TableCell>
                        <TableCell className="text-right">$250</TableCell>
                        <TableCell className="text-right">$250</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                    onClick={() => setShowCopayDialog(true)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Copay Schedule
                  </Button>
                </CardContent>
              </Card>

              {/* OOP Accumulator Tracking */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    OOP Accumulator Tracking
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Track deductible and out-of-pocket maximums for employees.
                  </p>
                  <div className="mb-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm">Avg. Deductible Met</span>
                      <span className="text-sm font-medium">45%</span>
                    </div>
                    <Progress value={45} className="h-2" />
                  </div>
                  <div className="mb-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm">Avg. OOP Max Met</span>
                      <span className="text-sm font-medium">12%</span>
                    </div>
                    <Progress value={12} className="h-2" />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                    onClick={() => setShowAccumulatorDialog(true)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Accumulator Details
                  </Button>
                </CardContent>
              </Card>

              {/* Integration Link */}
              <div className="lg:col-span-2">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between">
                    <span>
                      Import benefit data from HRIS systems, claims files, and HSA custodians via the Integration Workbench.
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/admin/integrations')}
                      className="ml-4 whitespace-nowrap"
                    >
                      Go to Integration Workbench
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* HSA/FSA Configuration Dialog */}
      <Dialog open={showHsaFsaDialog} onOpenChange={setShowHsaFsaDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-green-600" />
              HSA/FSA Configuration
            </DialogTitle>
            <DialogDescription>
              Configure Health Savings Account and Flexible Spending Account limits for your benefit plans.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Plan Selector */}
            <div className="space-y-2">
              <Label htmlFor="plan-select">Apply to Plan</Label>
              <Select
                value={selectedPlanForConfig?.id?.toString() || 'all'}
                onValueChange={(value) => {
                  if (value === 'all') {
                    setSelectedPlanForConfig(null);
                  } else {
                    const plan = medicalPlans.find(p => p.id === Number(value));
                    setSelectedPlanForConfig(plan || null);
                  }
                }}
              >
                <SelectTrigger id="plan-select">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All HDHP Plans</SelectItem>
                  {medicalPlans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.plan_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-1 sm:col-span-2">
                <h4 className="text-sm font-semibold mb-2">HSA Contribution Limits (2025 IRS)</h4>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hsa-individual">HSA Individual Limit</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="hsa-individual"
                    type="number"
                    value={hsaFsaConfig.hsaIndividualLimit}
                    onChange={(e) => setHsaFsaConfig(prev => ({ ...prev, hsaIndividualLimit: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hsa-family">HSA Family Limit</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="hsa-family"
                    type="number"
                    value={hsaFsaConfig.hsaFamilyLimit}
                    onChange={(e) => setHsaFsaConfig(prev => ({ ...prev, hsaFamilyLimit: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="catchup">Catch-Up Contribution (55+)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="catchup"
                    type="number"
                    value={hsaFsaConfig.catchUpContribution}
                    onChange={(e) => setHsaFsaConfig(prev => ({ ...prev, catchUpContribution: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="employer-contrib">Employer Annual Contribution</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="employer-contrib"
                    type="number"
                    value={hsaFsaConfig.employerContribution}
                    onChange={(e) => setHsaFsaConfig(prev => ({ ...prev, employerContribution: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <hr className="my-4 border-border" />
                <h4 className="text-sm font-semibold mb-2">FSA Limits</h4>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fsa-limit">Health Care FSA Limit</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="fsa-limit"
                    type="number"
                    value={hsaFsaConfig.fsaLimit}
                    onChange={(e) => setHsaFsaConfig(prev => ({ ...prev, fsaLimit: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dependent-fsa">Dependent Care FSA Limit</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="dependent-fsa"
                    type="number"
                    value={hsaFsaConfig.dependentCareFsaLimit}
                    onChange={(e) => setHsaFsaConfig(prev => ({ ...prev, dependentCareFsaLimit: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <hr className="my-4 border-border" />
                <h4 className="text-sm font-semibold mb-2">HSA Custodian</h4>
              </div>

              <div className="col-span-1 sm:col-span-2 space-y-2">
                <Label htmlFor="custodian">HSA Custodian</Label>
                <Select
                  value={hsaFsaConfig.hsaCustodian}
                  onValueChange={(value) => setHsaFsaConfig(prev => ({ ...prev, hsaCustodian: value }))}
                >
                  <SelectTrigger id="custodian">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HealthEquity">HealthEquity</SelectItem>
                    <SelectItem value="Optum Bank">Optum Bank</SelectItem>
                    <SelectItem value="Fidelity">Fidelity</SelectItem>
                    <SelectItem value="HSA Bank">HSA Bank</SelectItem>
                    <SelectItem value="Lively">Lively</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHsaFsaDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowHsaFsaDialog(false)}>
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HDHP Configuration Dialog */}
      <Dialog open={showHdhpDialog} onOpenChange={setShowHdhpDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-primary" />
              HDHP Configuration
            </DialogTitle>
            <DialogDescription>
              Configure High Deductible Health Plan requirements for HSA eligibility. These values must meet IRS minimums.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Plan Selector */}
            <div className="space-y-2">
              <Label htmlFor="hdhp-plan-select">Configure Plan</Label>
              <Select
                value={selectedPlanForConfig?.id?.toString() || ''}
                onValueChange={(value) => {
                  const plan = medicalPlans.find(p => p.id === Number(value));
                  setSelectedPlanForConfig(plan || null);
                }}
              >
                <SelectTrigger id="hdhp-plan-select">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {medicalPlans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.plan_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-1 sm:col-span-2">
                <h4 className="text-sm font-semibold mb-2">Minimum Deductible Requirements (2025 IRS)</h4>
              </div>

              <div className="space-y-2">
                <Label htmlFor="min-ded-ind">Min Deductible (Individual)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="min-ded-ind"
                    type="number"
                    value={hdhpConfig.minDeductibleIndividual}
                    onChange={(e) => setHdhpConfig(prev => ({ ...prev, minDeductibleIndividual: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">IRS minimum: $1,650</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="min-ded-fam">Min Deductible (Family)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="min-ded-fam"
                    type="number"
                    value={hdhpConfig.minDeductibleFamily}
                    onChange={(e) => setHdhpConfig(prev => ({ ...prev, minDeductibleFamily: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">IRS minimum: $3,300</p>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <h4 className="text-sm font-semibold mb-2 mt-4">Maximum Out-of-Pocket Limits</h4>
              </div>

              <div className="space-y-2">
                <Label htmlFor="max-oop-ind">Max OOP (Individual)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="max-oop-ind"
                    type="number"
                    value={hdhpConfig.maxOopIndividual}
                    onChange={(e) => setHdhpConfig(prev => ({ ...prev, maxOopIndividual: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">IRS maximum: $8,300</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="max-oop-fam">Max OOP (Family)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="max-oop-fam"
                    type="number"
                    value={hdhpConfig.maxOopFamily}
                    onChange={(e) => setHdhpConfig(prev => ({ ...prev, maxOopFamily: Number(e.target.value) }))}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">IRS maximum: $16,600</p>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <hr className="my-4 border-border" />
                <h4 className="text-sm font-semibold mb-2">Plan Options</h4>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="preventive-care"
                  checked={hdhpConfig.preventiveCareExempt}
                  onCheckedChange={(checked) => setHdhpConfig(prev => ({ ...prev, preventiveCareExempt: checked }))}
                />
                <Label htmlFor="preventive-care">Preventive Care Exempt from Deductible</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="embedded-ded"
                  checked={hdhpConfig.embeddedDeductible}
                  onCheckedChange={(checked) => setHdhpConfig(prev => ({ ...prev, embeddedDeductible: checked }))}
                />
                <Label htmlFor="embedded-ded">Embedded Individual Deductible</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHdhpDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowHdhpDialog(false)}>
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copay Schedule Dialog */}
      <Dialog open={showCopayDialog} onOpenChange={setShowCopayDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-orange-600" />
              Edit Copay Schedule
            </DialogTitle>
            <DialogDescription>
              Configure member cost-sharing for various service types across your benefit plans.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Plan Selector */}
            <div className="space-y-2">
              <Label htmlFor="copay-plan-select">Edit Schedule for Plan</Label>
              <Select
                value={selectedPlanForConfig?.id?.toString() || ''}
                onValueChange={(value) => {
                  const plan = medicalPlans.find(p => p.id === Number(value));
                  setSelectedPlanForConfig(plan || null);
                }}
              >
                <SelectTrigger id="copay-plan-select">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {medicalPlans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.plan_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service Type</TableHead>
                      <TableHead className="text-center">In-Network Copay</TableHead>
                      <TableHead className="text-center">Out-of-Network</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {copaySchedule.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.service}</TableCell>
                        <TableCell className="text-center">
                          <div className="relative inline-block w-24">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                            <Input
                              type="number"
                              value={row.inNetwork}
                              onChange={(e) => {
                                const newSchedule = [...copaySchedule];
                                newSchedule[index].inNetwork = Number(e.target.value);
                                setCopaySchedule(newSchedule);
                              }}
                              className="pl-5 h-8 text-center"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="relative inline-block w-24">
                            {row.outNetworkType === 'copay' && (
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                            )}
                            <Input
                              type="number"
                              value={row.outNetwork}
                              onChange={(e) => {
                                const newSchedule = [...copaySchedule];
                                newSchedule[index].outNetwork = Number(e.target.value);
                                setCopaySchedule(newSchedule);
                              }}
                              className={`h-8 text-center ${row.outNetworkType === 'copay' ? 'pl-5' : 'pr-5'}`}
                            />
                            {row.outNetworkType === 'percent' && (
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setCopaySchedule(prev => prev.filter((_, i) => i !== index))}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setCopaySchedule(prev => [...prev, { service: 'New Service', inNetwork: 0, outNetwork: 40, outNetworkType: 'percent' as const }])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service Type
                </Button>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopayDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowCopayDialog(false)}>
              Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OOP Accumulator Dialog */}
      <Dialog open={showAccumulatorDialog} onOpenChange={setShowAccumulatorDialog}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              OOP Accumulator Details
            </DialogTitle>
            <DialogDescription>
              View and manage deductible and out-of-pocket accumulator tracking for employees across all plans.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Plan Filter */}
            <div className="space-y-2">
              <Label htmlFor="accumulator-plan">Filter by Plan</Label>
              <Select
                value={selectedPlanForConfig?.id?.toString() || 'all'}
                onValueChange={(value) => {
                  if (value === 'all') {
                    setSelectedPlanForConfig(null);
                  } else {
                    const plan = medicalPlans.find(p => p.id === Number(value));
                    setSelectedPlanForConfig(plan || null);
                  }
                }}
              >
                <SelectTrigger id="accumulator-plan" className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  {medicalPlans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.plan_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground mb-1">Avg. Deductible Met</p>
                  <p className="text-3xl font-bold text-primary mb-2">45%</p>
                  <Progress value={45} className="h-2" />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground mb-1">Avg. OOP Max Met</p>
                  <p className="text-3xl font-bold text-secondary mb-2">12%</p>
                  <Progress value={12} className="h-2" />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground mb-1">Employees at OOP Max</p>
                  <p className="text-3xl font-bold text-destructive mb-2">8</p>
                  <p className="text-xs text-muted-foreground">of {plans[0]?.enrolled_count || 300} enrolled</p>
                </CardContent>
              </Card>
            </div>

            <h4 className="text-base font-semibold mb-2">Employee Accumulator Summary</h4>
            <Card>
              <CardContent className="pt-6 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Coverage Tier</TableHead>
                      <TableHead className="text-right">Deductible Used</TableHead>
                      <TableHead className="text-right">Deductible Limit</TableHead>
                      <TableHead className="text-right">% Met</TableHead>
                      <TableHead className="text-right">OOP Used</TableHead>
                      <TableHead className="text-right">OOP Limit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { id: 'EMP001', plan: 'Gold PPO', tier: 'Family', dedUsed: 800, dedLimit: 1000, oopUsed: 2500, oopLimit: 6000 },
                      { id: 'EMP002', plan: 'Silver HDHP', tier: 'Individual', dedUsed: 2000, dedLimit: 2000, oopUsed: 4500, oopLimit: 6000 },
                      { id: 'EMP003', plan: 'Gold PPO', tier: 'Employee Only', dedUsed: 250, dedLimit: 500, oopUsed: 800, oopLimit: 3000 },
                      { id: 'EMP004', plan: 'Silver HDHP', tier: 'Family', dedUsed: 3200, dedLimit: 4000, oopUsed: 8000, oopLimit: 12000 },
                      { id: 'EMP005', plan: 'Gold PPO', tier: 'Employee + Spouse', dedUsed: 1000, dedLimit: 1000, oopUsed: 6000, oopLimit: 6000 },
                    ].map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.plan}</TableCell>
                        <TableCell>{row.tier}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.dedUsed)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.dedLimit)}</TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant={row.dedUsed >= row.dedLimit ? 'default' : 'secondary'}
                          >
                            {Math.round((row.dedUsed / row.dedLimit) * 100)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(row.oopUsed)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.oopLimit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <div className="flex gap-2 mt-4">
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                Configure Alerts
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowAccumulatorDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
